import type { ConversationType } from "./conversationType.types";

/** The two tabs of every Gesprekken overview, as the API names them. */
export const CONVERSATION_TAB = {
  PLANNED: "planned",
  COMPLETED: "completed",
} as const;

export type ConversationTab =
  (typeof CONVERSATION_TAB)[keyof typeof CONVERSATION_TAB];

/** How many rows each tab holds, for the counts beside the tab labels. */
export type ConversationTabCounts = {
  planned: number;
  completed: number;
};

/** The student a gespreksverslag row names. Absent on an invitation row. */
export type ConversationRowStudent = {
  id: number;
  name: string;
  avatar: string | null;
};

/** De docent die het gesprek voerde. Null wanneer dat account verwijderd is. */
export type ConversationRowPartner = {
  name: string;
  avatar: string | null;
};

/**
 * One row of a Gesprekken table — the classroom tabs and the teacher's own overview
 * share it.
 *
 * Two kinds share the shape. A `conversation` is a gespreksverslag and names its one
 * student; an `invitation` is an ingepland gesprek and reports how many students the
 * caller scoped to: the class being viewed on a classroom tab
 * (CONVERSATION-CLASSROOM-SCOPE-001), and its own total on the teacher overview unless a
 * search narrowed it (CONVERSATION-TEACHER-SCOPE-001).
 */
export type ConversationRow = {
  id: number;
  kind: "conversation" | "invitation";
  name: string;
  /** Null only when the API sent something unparseable; the field itself is required. */
  date: Date | null;
  /**
   * The end of the window when the gesprek is planned for more than one leerling: `date`
   * is then its start. Null for a gesprek with one leerling, which is a single moment
   * (CONVERSATION-INVITATION-DATE-RANGE-001).
   */
  endDate: Date | null;
  conversationType: ConversationType;
  /** The Vak column; null when the gesprek is not about a vak. Both kinds carry it. */
  subjectShortage: string | null;
  student: ConversationRowStudent | null;
  /**
   * Wie het gesprek voerde. De klas-tabbladen tonen ook gesprekken van collega's, dus dit
   * is niet per definitie de kijker.
   */
  partner: ConversationRowPartner | null;
  /**
   * De afkorting van een docent wiens account verwijderd is — alles wat de Docent-kolom
   * dan nog kan tonen.
   */
  teacherShortage: string | null;
  /** Invitation rows only: how many students the caller scoped to. */
  studentsCount: number | null;
  /** Invitation rows only: how many of those finished their voorbereiding. */
  studentsPrepared: number | null;
  /**
   * The pill, as the API decided it: a `conversation` row wears a `ConversationStatus` and
   * an `invitation` row a `ConversationInvitationStatus`, since one covers a single leerling
   * and the other a whole group.
   */
  status: ConversationRowPillStatus;
  /**
   * An invitation whose students are still being resolved on the queue, so both counts
   * read 0 for reasons that have nothing to do with the gesprek. The table shows a
   * loading indicator instead of `0/0`. A conversation row waits on no queue and is
   * always `false`.
   */
  inProgress: boolean;
  /**
   * The queued delete cascade is still running: the row is on its way out but its
   * gesprekken are still there. Always false for a gespreksverslag, which deletes inline.
   */
  deleting: boolean;
};

/**
 * The pill on one leerling's gesprek. Derived from the date, the voorbereiding and the
 * verslag — never sent by the API, which ships the facts and no status
 * (CONVERSATION-SIDEBAR-STATUS-FILTER-001).
 *
 * The ladder that picks between them lives in `conversationStatus()`, and its SQL twin in
 * `StudentSidebarConversationRepository::applyStatus()`. The values equal the backend's
 * `ConversationStatus`, so a filter value travels from the pill to the query unchanged.
 */
export const CONVERSATION_STATUS = {
  /** Still ahead, and nothing was asked of the leerling. */
  PLANNED: "planned",
  /** Still ahead, and the voorbereiding has not been handed in. */
  WAIT_FOR_PREPARATION: "wait_for_preparation",
  /** Handed in before the gesprek started. */
  PREPARED: "prepared",
  /** Handed in after it started, while it was not yet over. */
  PREPARED_LATE: "prepared_late",
  /** Over, and the voorbereiding it needed never arrived. */
  WAIT_FOR_PREPARATION_LATE: "wait_for_preparation_late",
  /** Over, the voorbereiding is settled, and nobody wrote the gesprek up. */
  WAIT_FOR_NOTE: "wait_for_note",
  /**
   * A verslag from either side exists. Terminal, and deliberately not "completed", which is
   * what `ConversationTab` calls the Afgerond tab: the tab splits on the date, this says a
   * verslag was written.
   */
  CLOSED: "closed",
} as const;

export type ConversationStatus =
  (typeof CONVERSATION_STATUS)[keyof typeof CONVERSATION_STATUS];

/**
 * The pill on an ingepland gesprek, which covers many leerlingen at once and so cannot wear
 * a per-leerling status. Two states against the seven above, and both read the clock only:
 * the voorbereidingen and verslagen belong to the gesprekken below the planning.
 *
 * Afgerond is not one of them — that is the Ingepland/Afgerond tab, decided over every child,
 * so a planning that has taken place while a verslag is still missing wears Plaatsgevonden in
 * the Ingepland tab.
 */
export const CONVERSATION_INVITATION_STATUS = {
  /** The einddatum is still ahead. */
  PLANNED: "planned",
  /** The einddatum has passed. */
  TOOK_PLACE: "took_place",
} as const;

export type ConversationInvitationStatus =
  (typeof CONVERSATION_INVITATION_STATUS)[keyof typeof CONVERSATION_INVITATION_STATUS];

/**
 * The tag colours a pill can take (`styles/modules/_tags.scss`). Four for eight values —
 * the mapping itself is `CONVERSATION_STATUS_COLOR`.
 */
export type ConversationStatusColor = "blue" | "aqua" | "yellow" | "grey";

/** Either pill, for the row table that shows both kinds side by side. */
export type ConversationRowPillStatus =
  | ConversationStatus
  | ConversationInvitationStatus;
